<template>
	<div class="advanced-container">
		<ModalHeader
			:title="title"
			:button-text="getString('save')"
			@click-button="clickSave" />
		<div class="advanced-options">
			<AppList :items="options" >
				<template v-slot:header-quantity="{item}">
					<div class="list-item-header">
						<AppNumberInput double-decimal :symbol="currencyStore.currCurrency.symbol" :min="minFullPrice" :value="currencyStore.dbToActualValue(insertedFullPrice)" @changed-value="changedFullPrice" />
					</div>
				</template>
				<template v-slot:dropdown-vat="{item}">
					<AppList :items="vatOptions">
						<template v-slot:dropdown-vat-nature="{item}">
							<AppList :items="item.children">
								<template #dropdown-vat-select="{item}">
									<div class="row centered space-between ">
										<p class="list-item-title">{{item.title}}</p>
										<div class="left-portion">
											<p class="mr-10 mt-5 color-txt-2">{{item.selectedText}}</p>
											<AppButton class="right-item" small outline :text="item.buttonTitle"
													   :disabled="item.disabled" @click="item.callbackButton" />
										</div>
									</div>
								</template>
							</AppList>
						</template>
					</AppList>
				</template>
                <template v-slot:sku-edit="{item}">
                    <div class="list-item-header">
                        <p class="list-item-title">{{ item.title }}</p>
                        <AppTextInput v-if="!product.warehouse" ref="sku-input" :placeholder="getString('sku')" :value="product.sku" @changed-text="(v) => product.sku = v" />
                        <div class="sku-no-edit" v-else>
                            <p class="sku">{{ product.sku }}</p>
                            <p class="explain">{{ getString('warehouse_connected') }}</p>
                        </div>
                    </div>
                </template>
				<template v-slot:print-alias-edit="{item}">
					<div class="list-item-header">
						<p class="list-item-title">{{ item.title }}</p>
						<AppTextInput :placeholder="getString('print_alias')" :value="product.print_alias" @changed-text="(v) => product.print_alias = v" />
					</div>
				</template>
				<template v-slot:dropdown-cross-selling="{item}">
					<div class="row flex-end">
						<AppNumberInput ref="cross-selling-input" :label="getString('priority')" placeholder="0" :min="0" :max="100" :value="crossSelling" @changed-value="(v) => product.cross_selling_priority = v" />
					</div>
				</template>
				<template v-slot:dropdown-list="{item}">
					<div class="row">
						<AppList :items="item.items" />
					</div>
				</template>
			</AppList>
		</div>

		<AppBottomSheet :active="showVatNatureBottomSheet" @toggling-popup="togglePopup" :force-width="bottomSheetWidth">
			<VatNatureBottomSheet :categoryTempId="categoryTempId" @apply="updateNature"/>
		</AppBottomSheet>
		<AppBottomSheet :active="showVatNatureRegRefBottomSheet" @toggling-popup="togglePopupRegRef" :force-width="bottomSheetWidth">
			<VatNatureRegulatoryReferenceBottomSheet :nature="nature ?? ''" :reg-ref-index="regRefIndex ?? 0" @apply="updateRegRef"/>
		</AppBottomSheet>
	</div>
</template>

<script>
import AppList from "@/shared/components/AppList.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";

import {useEpsonStore, useMenuStore, useUserStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import VatNatureRegulatoryReferenceBottomSheet from "@/console-new/components/config/menu/VatNatureRegulatoryReferenceBottomSheet.vue";
import VatNatureBottomSheet from "@/console-new/components/config/menu/VatNatureBottomSheet.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let menuStore = useMenuStore();
		let userStore = useUserStore();
		let notificationStore = useNotificationStore();
		let epsonStore = useEpsonStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, currencyStore, epsonStore, menuStore, userStore};
	},
	emits: ["save-clicked"],
	name: "AdvancedOptionsEditor",
	mixins: [WindowWidth],
	components: {
		AppBottomSheet,
		VatNatureBottomSheet,
		VatNatureRegulatoryReferenceBottomSheet,
		AppNumberInput, AppList, AppButton, AppSelect, AppPopup, ModalHeader, AppTextInput},
	props: {
		itemTempId: {
			type: String,
			required: true
		},
		categoryTempId: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			activeDiscount: false,
			insertedFullPrice: 0,
			showVatNatureBottomSheet: false,
			showVatNatureRegRefBottomSheet: false,
			nature: null,
			regRefIndex: null,
			reg: null
		};
	},
	computed: {
		title() {
			return this.getString("title_category_advanced", [this.product.name]);
		},
		product() {
			return this.menuStore.modItemFromTempIds(this.categoryTempId, this.itemTempId);
		},
		vats() {
			let vats = this.epsonStore.countryVats["IT"];
			if(this.userStore.currBusiness && this.epsonStore.countryVats.hasOwnProperty(this.userStore.currBusiness.country)) {
				vats = this.epsonStore.countryVats[this.userStore.currBusiness.country];
			}

			return vats;
		},
		options() {
			let currentOptions = [];
			if (this.userStore.currPlans.counter || this.userStore.currPlans.order_pay) {

				currentOptions.push({
					title: this.getString('vat_value'),
					switch: true,
					active: this.product.vat !== null,
					dropdownSlot: "dropdown-vat",
					callback: (v) => {
						if(v) {
							this.product.vat = this.vats[0];
						} else {
							this.product.vat = null;
							this.product.natura_iva = null;
						}
					},
				});

			}
			currentOptions.push(
				{ title: this.getString("product_sku"), customSlot: "sku-edit" },
				{ title: this.getString("print_alias"), customSlot: "print-alias-edit" },
				{
					title: this.getString("price_without_discount"),
					dropdownSlot: "header-quantity",
					switch: true,
					active: this.activeDiscount,
					callback: this.switchDiscount
				},
			);
			if ( this.userStore.currPlans.order_pay ) {
				currentOptions.push(
					{
						title: this.getString("cannot_be_ordered"),
						switch: true,
						active: this.product.orderability !== "standard",
						callback: (res) => this.product.orderability = res ? "disabled" : "standard",
						dropdownSlot: "dropdown-list",
						items: [
							{
								title: this.getString("hide_price"),
								switch: true,
								active: this.product.orderability === "no_price",
								callback: (res) => this.product.orderability = res ? "no_price" : "disabled"
							},
						]
					}, {
						title: this.getString("cross_selling"),
						switch: true,
						active: this.product.cross_selling_priority !== null,
						dropdownSlot: "dropdown-cross-selling",
						callback: this.crossSellingSwitched
					},{
						title: this.getString("variation_redeemable"),
						switch: true,
						active: this.product.redeemable_variations,
						callback: active => {this.product.redeemable_variations = active}
					}
				);
			}
			return currentOptions;
		},
		minFullPrice() {
			return this.product.price ? this.currencyStore.dbToActualValue(this.product.price + 1) : 0;
		},
		vat() {
			return this.product.vat !== null ? Number(this.product.vat) : 0;
		},
		vatOptions() {
			let index = this.vats.findIndex(v => parseFloat(v) === parseFloat(this.product.vat));

			if(index === -1) {
				index = 0;
			}

			return [
				{
					title: this.getString('vat_value'),
					selectValues: this.vats,
					selectedIndex: index,
					active: parseFloat(this.product.vat) === 0,
					callbackSelect: this.vatSwitched,
					dropdownSlot: 'dropdown-vat-nature',
					children: [
						{
							title: this.getString('vat_nature'),
							customSlot: 'dropdown-vat-select',
							buttonTitle: this.getString('vat_nature_btn'),
							selectedText: this.nature,
							disabled: false,
							callbackButton: () => this.showVatNatureBottomSheet = true
						},
						{
							title: this.getString('vat_nature_regulations'),
							customSlot: 'dropdown-vat-select',
							buttonTitle: this.getString('vat_nature_regulations_btn'),
							selectedText: this.reg,
							disabled: this.nature === null,
							callbackButton: () => this.showVatNatureRegRefBottomSheet = true
						}
					]
				}
			]
		},
		crossSelling() {
			return this.product.cross_selling_priority !== null ? Number(this.product.cross_selling_priority) : 0;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else {
				return "960px";
			}
		}
	},
	methods: {
		changedFullPrice(newVal) {
			this.insertedFullPrice = this.currencyStore.actualToDbValue(newVal);
		},
		vatSwitched(index) {
			this.product.vat = this.vats[index];
		},
		crossSellingSwitched(active) {
			if(active) {
				this.product.cross_selling_priority = this.$refs["cross-selling-input"].currentValue;
			} else {
				this.product.cross_selling_priority = null;
			}
		},
		clickSave() {
			if(this.activeDiscount && this.insertedFullPrice <= this.product.price) {
				this.showNotification("full-price-low");
				return;
			}
			this.product.full_price = this.activeDiscount ? this.insertedFullPrice : null;
			if(parseInt(this.product.vat) === 0) {
				if(this.nature !== null && this.regRefIndex !== null) {
					this.product.natura_iva = this.nature+'.'+this.regRefIndex;
				}
				else {
					this.showNotification('vat-nature-not-set')
					return;
				}
			}
			this.$emit('save-clicked');
		},
		switchDiscount(active) {
			if(active) {
				if(!this.product.full_price || this.product.full_price <= this.product.price) {
					this.insertedFullPrice = this.product.price + 1;
				}
				this.activeDiscount = true;
			} else {
				this.activeDiscount = false;
			}
		},
		togglePopup(active) {
			if (!active) {
				this.showVatNatureBottomSheet = active;
			}
		},
		togglePopupRegRef(active) {
			if (!active) {
				this.showVatNatureRegRefBottomSheet = active;
			}
		},
		updateNature(nature) {
			this.nature = nature;
			this.regRefIndex = null;
			this.reg = null;
			this.togglePopup(false);
		},
		updateRegRef(regRefIndex, reg) {
			this.regRefIndex = regRefIndex;
			this.reg = reg;
			this.togglePopupRegRef(false);
		}
	},
	mounted() {
		if(this.product.full_price && this.product.full_price > this.product.price) {
			this.activeDiscount = true;
			this.insertedFullPrice = this.product.full_price;
		} else {
			this.activeDiscount = false;
		}

		if(this.product.natura_iva !== null) {
			let parsedNature = this.epsonStore.parseVatNature(
				this.userStore.currBusiness.country,
				this.product.natura_iva
			);

			this.nature = parsedNature.nature;
			this.regRefIndex = parsedNature.regRefIndex;
			this.reg = parsedNature.reg;
		}
	}
}
</script>

<style lang="scss" scoped>

.advanced-options {
	.left-portion {
		display: flex;
		flex-direction: row;
	}

	.list-item-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		width: 100%;

		.list-item-title {
			margin-right: 5px;
		}

		.input-wrapper {
			margin-left: auto;
		}

        .sku-no-edit {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            justify-content: center;
            margin-left: auto;
            padding-left: 12px;

            .sku {
                font-size: 14px;
                font-weight: 500;
            }

            .explain {
                font-size: 12px;
                color: $primary;
            }
        }
	}

}

</style>
